import { useState } from "react";
import type { ErrorLog, AppSettings } from "../../types";
import { useTheme } from "../../theme";
import ErrorFeed from "./ErrorFeed";

type Phase = "idle" | "connected" | "ended";

interface PanelProps {
  phase: Phase;
  isRecording: boolean;
  ending: boolean;
  barHeights: number[];
  elapsedSeconds: number;
  errors: ErrorLog[];
  settings: AppSettings | null;
  focusLabel: string | null;
}

/** The one target a focus session drills, pinned for the whole session. */
function FocusChip({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 max-w-full bg-accent/10 border border-accent/30 rounded-full px-3 py-1">
      <span className="text-[0.575rem] uppercase tracking-[0.14em] text-accent font-semibold shrink-0">Focus</span>
      <span className="text-[0.75rem] font-medium text-ink truncate">{label}</span>
    </div>
  );
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

/** Voice amplitude 0..1 derived from the playback visualiser bars (Elena's voice only). */
function amplitude(barHeights: number[]): number {
  if (barHeights.length === 0) return 0;
  const avg = barHeights.reduce((a, b) => a + b, 0) / barHeights.length;
  return Math.min(1, Math.max(0, (avg - 3) / 15));
}

function statusFor(phase: Phase, isRecording: boolean, ending: boolean, speaking: boolean) {
  if (ending) return { text: "Wrapping up…", dot: "bg-warning animate-pulse-soft" };
  if (phase === "idle") return { text: "Ready when you are", dot: "bg-border" };
  if (phase === "ended") return { text: "Session complete", dot: "bg-border" };
  if (isRecording) return { text: "Listening to you", dot: "bg-accent animate-pulse-soft" };
  if (speaking) return { text: "Speaking", dot: "bg-success animate-pulse-soft" };
  return { text: "In conversation", dot: "bg-success" };
}

export function Avatar({ amp, size = 96 }: { amp: number; size?: number }) {
  const [theme] = useTheme();
  const portrait = theme === "light" ? "/elena-light3.png" : "/elena-dark3.png";
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      {/* voice ring — scales with Elena's live amplitude */}
      <div
        className="absolute -inset-1.5 rounded-full border-2 border-success transition-all duration-100 ease-out"
        style={{ transform: `scale(${1 + amp * 0.14})`, opacity: 0.1 + amp * 0.75 }}
      />
      {/* illustrated portrait — cropped to a circular headshot */}
      <div className="w-full h-full rounded-full overflow-hidden animate-breathe select-none shadow-[0_12px_28px_-14px_rgba(20,90,45,0.5)]">
        <img
          src={portrait}
          alt="Elena"
          draggable={false}
          className="w-full h-full object-cover"
          style={{ transform: "scale(1.3)", transformOrigin: "50% 30%" }}
        />
      </div>
    </div>
  );
}

function Waveform({ barHeights }: { barHeights: number[] }) {
  return (
    <div className="flex items-center justify-center gap-[3px] h-6" aria-hidden>
      {barHeights.map((h, i) => (
        <div
          key={i}
          className="w-[3px] rounded-full bg-accent/60 transition-[height] duration-[90ms]"
          style={{ height: `${Math.min(22, Math.max(3, h))}px` }}
        />
      ))}
    </div>
  );
}

function InfoChip({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-0.5 bg-surface2/60 rounded-[10px] px-2 py-2 flex-1 min-w-0">
      <span className={`text-[0.8125rem] font-medium text-ink truncate max-w-full ${mono ? "font-mono tabular-nums" : ""}`}>
        {value}
      </span>
      <span className="text-[0.575rem] uppercase tracking-[0.14em] text-text-tertiary font-medium">{label}</span>
    </div>
  );
}

/** Desktop: left-hand tutor panel. */
export default function TutorPanel({ phase, isRecording, ending, barHeights, elapsedSeconds, errors, settings, focusLabel }: PanelProps) {
  const amp = amplitude(barHeights);
  const status = statusFor(phase, isRecording, ending, amp > 0.04);

  return (
    <aside className="hidden lg:flex w-[320px] xl:w-[340px] flex-col flex-shrink-0 border-r border-border-soft bg-surface2/30 overflow-hidden">
      <div className="flex flex-col items-center text-center px-6 pt-9 pb-6 gap-3">
        <Avatar amp={amp} />
        <div>
          <div className="font-display font-bold tracking-[-0.02em] text-[1.5rem] text-ink leading-tight">Elena</div>
          <div className="text-[0.8125rem] text-text-tertiary italic">la tua tutor d'italiano</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${status.dot}`} />
          <span className="text-[0.6875rem] uppercase tracking-[0.14em] text-text-tertiary font-medium">{status.text}</span>
        </div>
        <Waveform barHeights={barHeights} />
      </div>

      <div className="px-6">
        {phase !== "idle" ? (
          <div className="text-center">
            <div className="font-mono text-[1.75rem] font-semibold tabular-nums text-ink">{formatTime(elapsedSeconds)}</div>
            <div className="text-[0.625rem] uppercase tracking-[0.14em] text-text-tertiary font-medium mt-1">Time</div>
            {focusLabel && <div className="mt-4"><FocusChip label={focusLabel} /></div>}
          </div>
        ) : (
          <div className="flex gap-2">
            {settings && <InfoChip label="Level" value={settings.tutor_level} />}
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 min-h-0 px-6 pt-7 pb-6 gap-3">
        <div className="flex items-baseline justify-between">
          <span className="section-label">Live feedback</span>
          {errors.length > 0 && (
            <span className="text-[0.6875rem] font-mono tabular-nums text-text-tertiary">{errors.length}</span>
          )}
        </div>
        {errors.length === 0 ? (
          <p className="text-[0.8125rem] text-text-tertiary italic leading-relaxed">
            Nothing yet. When Elena quietly corrects a mistake, it appears here — the conversation never stops for it.
          </p>
        ) : (
          <ErrorFeed errors={errors} />
        )}
      </div>
    </aside>
  );
}

/** Mobile: compact strip above the chat with expandable feedback. */
export function TutorStrip({ phase, isRecording, ending, barHeights, elapsedSeconds, errors, focusLabel }: PanelProps) {
  const [showFeedback, setShowFeedback] = useState(false);
  const amp = amplitude(barHeights);
  const status = statusFor(phase, isRecording, ending, amp > 0.04);

  return (
    <div className="lg:hidden flex-shrink-0 border-b border-border-soft bg-surface/50">
      <div className="flex items-center gap-3 px-5 py-2.5">
        <Avatar amp={amp} size={38} />
        <div className="flex flex-col min-w-0">
          <span className="font-display font-bold tracking-[-0.02em] text-[1.05rem] text-ink leading-tight">Elena</span>
          <span className="flex items-center gap-1.5 text-[0.625rem] uppercase tracking-[0.12em] text-text-tertiary font-medium">
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            {status.text}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {phase !== "idle" && (
            <span className="text-[0.8125rem] font-mono tabular-nums text-text-secondary">{formatTime(elapsedSeconds)}</span>
          )}
          <button
            className={`text-[0.6875rem] font-semibold rounded-full px-2.5 py-1 transition-colors duration-200 ${
              errors.length > 0
                ? showFeedback
                  ? "bg-accent text-white"
                  : "bg-accent/20 text-accent"
                : "text-text-tertiary opacity-50"
            }`}
            onClick={() => setShowFeedback((v) => !v)}
            disabled={errors.length === 0}
          >
            {errors.length} {errors.length === 1 ? "note" : "notes"}
          </button>
        </div>
      </div>
      {focusLabel && phase !== "idle" && (
        <div className="px-5 pb-2.5 -mt-0.5"><FocusChip label={focusLabel} /></div>
      )}
      {showFeedback && errors.length > 0 && (
        <div className="max-h-44 overflow-y-auto px-5 pb-3">
          <ErrorFeed errors={errors} />
        </div>
      )}
    </div>
  );
}
